import { checkAbaOrder } from '@/api/aba-api';
import { cancelOrder } from '@/api/order-api';
import { fetchEnableABA } from '@/api/setting-api';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useBoolean } from '@/hooks/use-boolean';
import { useOrderConversation } from '@/hooks/use-order-conversation';
import { useOrderDetailsActions } from '@/hooks/use-order-details-actions';
import { useOrderDetailsData } from '@/hooks/use-order-details-data';
import { useOrderDialogs } from '@/hooks/use-order-dialogs';
import { toast } from '@/hooks/use-toast';
import { routes } from '@/routes/route-constants';
import { calculateOrderTotals } from '@/utils/calc-order';
import { Dialog } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useParams } from 'wouter';
import MyContainer from '../../components/MyContainer';
import { OrderDetails } from '../../components/orders/OrderDetails';
import { OrderDetailsChat } from '../../components/orders/OrderDetailsChat';
import { OrderDetailsDialogs } from '../../components/orders/OrderDetailsDialogs';
import { OrderDetailsError } from '../../components/orders/OrderDetailsError';
import { OrderDetailsLoading } from '../../components/orders/OrderDetailsLoading';
import PublicOrderDetailPage from './public-order-detail-page';

interface OrderDetailPageProps {
  orderId?: string;
  onClose?: () => void;
}

export default function OrderDetailPage({ orderId, onClose }: OrderDetailPageProps) {
  const { id } = useParams();

  const effectiveOrderId = orderId ?? id;
  const [, setLocation] = useLocation();
  const { user } = useAuth();

  // Public order dialog state
  const isOpenPublicOrderDialog = useBoolean(false);

  const {
    order,
    customer,
    orderItems,
    payments,
    orderReferences,
    fulfillment,
    orderStateHistory,
    isLoading,
    error,
    refresh,
  } = useOrderDetailsData(effectiveOrderId ?? null);

  // Actions hook
  const {
    onReadyForDelivery,
    onDelivered,
    handleAdjustmentSubmit,
    handleConfirmPayment,
    submitting,
  } = useOrderDetailsActions(effectiveOrderId ?? null, refresh);

  // Conversation hook — only pull Facebook conversation if order is from Facebook
  const { isFacebookSource, shouldShowChat, currentConversation, facebookId, isInactive } =
    useOrderConversation(customer, user?.facebookPage?.pageId, {
      enabled: Boolean(!isLoading && order),
      orderSource: order?.source ?? null,
    });

  // Dialogs hook
  const {
    isOpenningInvoiceDialog,
    setIsOpenningInvoiceDialog,
    autoPrint,
    handleDownloadInvoice,
    handlePrintInvoice,
    isOpenningReceipt,
    setIsOpenningReceipt,
    handleShowReceipt,
    openPaymentDialog,
    setOpenPaymentDialog,
    isCOD,
    handleCapturePaymentClick,
    handleCodClick,
    showAdjustDialog,
    setShowAdjustDialog,
    adjustMode,
    openRefund,
    openReturn,
    openAssignToUser,
    showAssignToUserDialog,
    onAssignToUser,
    setShowAssignToUserDialog,
  } = useOrderDialogs();

  // Derived values
  const deliveryFee = parseFloat(order?.deliveryFee || '2.0');
  const { total, subtotal, shippingFee } = useMemo(() => {
    return calculateOrderTotals({
      items: orderItems,
      discountPercentage: Number(order?.discountPercentage ?? 0),
      deliveryFee: deliveryFee,
      taxRate: 0,
    });
  }, [orderItems, order?.discountPercentage, deliveryFee]);

  const totalPaid = useMemo(() => {
    return payments.reduce((sum, p) => sum + Number(p.amount), 0);
  }, [payments]);

  const paymentMethod =
    payments.length > 0 ? payments[payments.length - 1].method : 'Not specified';

  const isABA = useCallback(async () => {
    try {
      const res = await fetchEnableABA();
      return res.data || false;
    } catch {
      return false;
    }
  }, []);

  const checkedAbaOrderIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!order?.id || order?.paymentStatus !== 'PENDING') return;
    if (checkedAbaOrderIdRef.current === order.id) return;

    isABA().then((enabled) => {
      if (!enabled || !order?.id) return;
      if (checkedAbaOrderIdRef.current === order.id) return;
      checkedAbaOrderIdRef.current = order.id;

      checkAbaOrder(order.id)
        .then((res) => {
          if (res?.success) {
            refresh();
          } else {
            console.log(`ABA check status for order ${order.id} not found or unsuccessful:`, res?.message || res);
          }
        })
        .catch((err) => {
          console.log(`ABA check status for order ${order.id} error:`, err);
        });
    });
  }, [order?.id, order?.paymentStatus, isABA, refresh]);

  const updatedFulfillment = useMemo(() => {
    if (!fulfillment) {
      return {
        method: 'DELIVERY_CITY',
        carrier: 'VIREAK_BUNTHAM',
        price: '2.0',
      } as any;
    }
    const carrier = fulfillment.carrier;
    const isUnknown = !carrier || String(carrier) === 'UNKNOWN' || String(carrier) === 'unknown';
    return {
      ...fulfillment,
      carrier: isUnknown ? 'VIREAK_BUNTHAM' : carrier,
    };
  }, [fulfillment]);

  // Handlers
  const hasPushedHistoryRef = useRef(false);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }
    window.history.back();
  }, [onClose]);

  const handleConfirmOrder = useCallback(() => {
    refresh();
  }, [refresh]);

  const handleViewProfile = useCallback(() => {
    if (customer?.id) {
      setLocation(routes.customer.detail(customer.id));
    }
  }, [customer?.id, setLocation]);

  const handleCancelOrder = useCallback(async () => {
    if (!order?.id) return;
    const resp = await cancelOrder(order?.id);
    if (resp.success) {
      toast({ title: 'Order Cancelled', description: 'Order has been cancelled successfully.' });
      refresh();
    } else {
      toast({
        title: 'Error',
        description: resp.message || 'Something went wrong',
        variant: 'destructive',
      });
    }
  }, [refresh, order?.id]);

  // Handle browser back button to call onClose
  useEffect(() => {
    // Push a dummy history state on mount
    if (!hasPushedHistoryRef.current) {
      window.history.pushState({ orderDetailPage: true }, '', window.location.href);
      hasPushedHistoryRef.current = true;
    }

    const handlePopState = (event: PopStateEvent) => {
      // Prevent default browser navigation
      event.preventDefault();

      // Call onClose if provided, otherwise go back
      if (onClose) {
        onClose();
      } else {
        // If no onClose, allow normal back navigation
        return;
      }

      // Push state again to maintain current URL
      window.history.pushState({ orderDetailPage: true }, '', window.location.href);
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);

      // Clean up: remove the dummy history entry when unmounting
      if (hasPushedHistoryRef.current && window.history.state?.orderDetailPage) {
        window.history.back();
      }
    };
  }, [onClose]);

  // Early returns — loading must be checked BEFORE order, because order is
  // null while the fetch is in-flight, which would otherwise show the error screen.
  if (!effectiveOrderId) {
    return <OrderDetailsError error={null} noId={true} />;
  }

  if (isLoading) {
    return <OrderDetailsLoading />;
  }

  if (!order) {
    return <OrderDetailsError error={error} noId={false} />;
  }

  return (
    <MyContainer
      title={order.orderNumber}
      subtitle="View and manage your order detail"
      disableContentPadding={true}
      actions={
        <Button size="sm" onClick={isOpenPublicOrderDialog.onTrue}>
          View Public Order
        </Button>
      }
      onBack={handleClose}
    >
      <div>
        <OrderDetails
          order={order}
          fulfillment={updatedFulfillment}
          customer={customer}
          orderItems={orderItems}
          orderReferences={orderReferences}
          orderStateHistory={orderStateHistory}
          payments={payments}
          paymentMethod={paymentMethod}
          onDownloadInvoice={handleDownloadInvoice}
          onPrintInvoice={handlePrintInvoice}
          onShowReceipt={handleShowReceipt}
          onConfirmOrder={handleConfirmOrder}
          onCancelOrder={handleCancelOrder}
          onViewProfile={handleViewProfile}
          onRefresh={refresh}
          onReloadOrder={refresh}
          onReadyForDelivery={onReadyForDelivery}
          onDelivered={onDelivered}
          onMarkAsCOD={handleCodClick}
          onCapturePayment={handleCapturePaymentClick}
          onRefundOrder={openRefund}
          onReturnOrder={openReturn}
          onAssignToUser={openAssignToUser}
        />

        {isFacebookSource && customer && (
          <OrderDetailsChat
            isFacebookSource={isFacebookSource}
            shouldShowChat={shouldShowChat}
            currentConversation={currentConversation}
            facebookId={facebookId}
            customerName={customer.name}
            isInactive={isInactive}
          />
        )}

        <OrderDetailsDialogs
          isOpenningInvoiceDialog={isOpenningInvoiceDialog}
          setIsOpenningInvoiceDialog={setIsOpenningInvoiceDialog}
          autoPrint={autoPrint}
          customer={customer}
          order={order}
          orderItems={orderItems}
          company={user?.store!}
          isOpenningReceipt={isOpenningReceipt}
          setIsOpenningReceipt={setIsOpenningReceipt}
          total={total}
          shippingFee={shippingFee}
          subtotal={subtotal}
          openPaymentDialog={openPaymentDialog}
          setOpenPaymentDialog={setOpenPaymentDialog}
          isCOD={isCOD}
          totalPaid={totalPaid}
          onConfirmPayment={handleConfirmPayment}
          showAdjustDialog={showAdjustDialog}
          setShowAdjustDialog={setShowAdjustDialog}
          adjustMode={adjustMode}
          submitting={submitting}
          showAssignUserDialog={showAssignToUserDialog}
          setShowAssignUserDialog={setShowAssignToUserDialog}
          onAssignUser={async (userId: string, orderId: string) => {
            const result = await onAssignToUser(userId, orderId);
            refresh();
            return result;
          }}
          onAdjustmentSubmit={handleAdjustmentSubmit}
        />

        {/* Public Order Dialog */}
        <Dialog
          open={isOpenPublicOrderDialog.value}
          onClose={isOpenPublicOrderDialog.onFalse}
          fullScreen
        >
          <PublicOrderDetailPage orderId={effectiveOrderId} />
        </Dialog>
      </div>
    </MyContainer>
  );
}
